<template>
    <tr class="item-detail">
        <td colspan="3" class="versions-container">
            <slide-up-modal
                v-if="fileToDelete"
                ref="fileDeletionModal"
                modal-id="fileDeletionModal"
                modal-title="Suppression de fichier"
                @modal-closed="hideFileDeletionModal"
            >
                <p>
                    Êtes-vous certain de vouloir supprimer le fichier 
                    <strong>{{ fileToDelete.file_name }}</strong> ? <br>
                    Cette action est irréversible.
                </p>

                <div class="actions" style="margin-top: 20px;">
                    <button class="btn btn-complete btn-sm" @click="performFileDeletion">Valider</button>
                    <button class="btn btn-default btn-sm" @click="hideFileDeletionModal" style="margin-left: 5px;">Annuler</button>
                </div>
            </slide-up-modal>

            <div v-if="!componentIsLoading" class="wrapper">
                <div class="buttons-container">
                    <button class="btn btn-complete btn-filled btn-sm" @click="$refs.file.click()">
                        <i class="fa fa-upload"></i>
                        <span>Ajouter une pièce</span>
                    </button>
                </div>

                <div v-if="hasFiles">
                    <div class="files-list">
                        <draggable v-model="internalDocument.customer_internal_document_files" @change="onFileOrderChange($event)">
                            <div v-for="(file, idx) in internalDocumentFiles" :key="'internal-document-file-' + idx" class="file-item">
                                <div>
                                    <p class="index">Pièce n° {{ idx + 1 }}<span>, créé le {{ file.created_at | moment-date-compact }}</span></p>
            
                                    <p class="name">{{ truncate(file.file_name, 40) }}</p>
                                </div>
    
                                <div class="buttons">
                                    <button class="btn btn-sm" @click="displayFileDeletionModal(file)">
                                        <i class="fa fa-trash" style="margin: 0;"></i>
                                    </button>
                                    
                                    <a :href="getFileStreamUrl(file)" target="_blank" class="btn btn-complete btn-sm" style="margin-left: 5px;">
                                        <i class="fa fa-eye"></i>
                                        <span>Voir</span>
                                    </a>
                                </div>
                            </div>
                        </draggable>
                    </div>
                </div>

                <p v-else>
                    Aucun fichier n'a été ajouté à ce document
                </p>
            </div>

            <component-loader v-else></component-loader>

            <input type="file" accept=".jpg, .png, .jpeg, .pdf" ref="file" class="hidden" @change="onFileInputChanged($event)">
        </td>
    </tr>
</template>

<script type="text/babel">
import {mapState, mapActions} from "vuex"
import api from 'store/api'
import SlideUpModal from 'components/Modals/SlideUpModal'
import Draggable from "vuedraggable"
import ComponentLoader from 'components/ComponentLoader'
import customerInternalDocumentFilesMixin from 'mixins/customerInternalDocumentFiles'

export default {
    name:"DocumentItemDetail",
    
    props: {
        index: {
            type: Number,
            required: true
        },
        customerId: {
            type: Number,
            required: true
        },
        internalDocumentId: {
            type: Number,
            required: true
        }
    },

    mixins: [
        customerInternalDocumentFilesMixin
    ],

    components: {
        SlideUpModal,
        Draggable,
        ComponentLoader
    },

    data () {
        return {
            componentIsLoading: false,
            internalDocument: null,
            fileToUpload: null,
            fileToDelete: null
        }
    },

    mounted() {
        this.fetchInternalDocument()
    },

    computed: {
        hasFiles() {
            return this.internalDocument != null && this.internalDocument.has_files
        },
        internalDocumentFiles() {
            return this.internalDocument != null ? this.internalDocument.customer_internal_document_files : []
        },
        orderedDocuments () {
            return _.sortBy(this.internalDocument.customer_internal_document_files, (file) => file.order)
        }
    },

    methods: {
        ...mapActions("customers", ["updateCustomerInternalDocumentFileOrder"]),

        fetchInternalDocument() {
            this.componentIsLoading = true

            return api.getCustomerInternalDocument(this.customerId, this.internalDocumentId, {with: 'customer_internal_document_files'})
                .then((response) => {
                    this.internalDocument = response.data.customer_internal_document
                    this.componentIsLoading = false
                })
        },
        onFileInputChanged(event) {
            this.fileToUpload = event.target.files[0]
        },
        uploadFile() {
            this.componentIsLoading = true

            let formData = new FormData()
            formData.append('customer_internal_document_id', this.internalDocument.id)
            formData.append('document_name', this.documentName)
            formData.append('document_file', this.fileToUpload, this.fileToUpload.name)

            return api.storeCustomerInternalDocumentFile(this.internalDocument.customer_id, formData)
                .then(response => {
                    this.$emit('file-uploaded', this.index)
                })
                .catch(error => {
                    this.addNotification("Une erreur s'est produite. Merci de réessayer plus tard", 'error')

                    this.componentIsLoading = false
                })
                .then(() => {
                    this.resetFileToUpload()
                })
        },
        resetFileToUpload() {
            this.$refs.file.value = ''
            this.fileToUpload = null
        },
        displayFileDeletionModal(file) {
            this.fileToDelete = file
        },
        hideFileDeletionModal() {
            this.fileToDelete = null
        },
        performFileDeletion() {
            if (this.fileToDelete) {
                this.$refs.fileDeletionModal.isLoading = true

                return api.deleteCustomerInternalDocumentFile(this.internalDocument.id, this.fileToDelete.id)
                    .then(response => {
                        if (this.internalDocumentFiles.length === 1) {
                            this.$emit('last-file-deleted', this.index)
                        } else {
                            this.$emit('file-deleted', this.index)
                        }
                    })
                    .catch(() => {
                        this.addNotification("Une erreur s'est produite, merci de réessayer", 'error')
                    })
                    .then(() => {
                        this.hideFileDeletionModal()
                    })
            }
        },
        onFileOrderChange(event) {
            if (event) {
                this.updateCustomerInternalDocumentFileOrder({
                    customer_internal_document_id: this.internalDocument.id,
                    customer_internal_document_file_id: event.moved.element.id,
                    params: {
                        order: this.orderedDocuments[event.moved.newIndex].order
                    }
                })
            }
        }
    },

    watch: {
        fileToUpload(newValue, oldValue) {
            if (newValue) {
                // Type validation
                const re = /(\.jpg|\.jpeg|\.png|\.pdf)$/i;

                if (!re.exec(this.fileToUpload.name)) {
                    this.resetFileToUpload()

                    this.addNotification('Type de fichier invalide : seuls les JPG, PNG et PDF sont acceptés', 'error')

                    return
                }

                // Size validation
                if (this.fileToUpload.size / 1024 / 1024 > 30) {
                    this.resetFileToUpload()

                    this.addNotification('Taille maximale de fichier dépassée', 'error')

                    return
                }

                this.uploadFile()
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    td.versions-container {
        padding: 0;
        background-color: #f0f0f0;

        p {
            margin: 0;
            font-size: inherit;
        }

        .wrapper {
            padding: 20px;

            .buttons-container {
                display: flex;
                justify-content: end;
                align-items: center;
            }
    
            .files-list {
                margin-top: 10px;

                > div {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0,1fr));
                    gap: 10px;
                }
    
                .file-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background-color: #fff;
                    padding: 6px 8px;
                    margin-bottom: 6px;
                    border-radius: 3px;
                    cursor: move;
                    cursor: grab;
    
                    &:active {
                        cursor: grabbing;
                    }
    
                    .index {
                        font-weight: 600;
                        font-size: 16px;
                        color: #555;
    
                        span {
                            font-size: 14px;
                            color: #888;
                        }
                    }
                    .name {
                        font-size: 14px;
                        color: #777;
                    }
                }
            }
        }
    }
</style>


// WEBPACK FOOTER //
// client/components/Customer/InternalDocuments/ItemDetail.vue